const BYTES_PER_KILOBYTE = 1024;
const BYTES_PER_MEGABYTE = BYTES_PER_KILOBYTE * BYTES_PER_KILOBYTE;
const BYTES_PER_GIGABYTE = BYTES_PER_MEGABYTE * BYTES_PER_KILOBYTE;

export const MAX_UPLOAD_SIZE_PERFORMANCE_VISUALIZATION = 512 * BYTES_PER_KILOBYTE;
export const ALLOWED_FILE_TYPES_PERFORMANCE_VISUALIZATION = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
export const MAX_UPLOAD_SIZE_AVATAR_PICTURE = 512 * BYTES_PER_KILOBYTE;
export const ALLOWED_FILE_TYPES_AVATAR_PICTURE = ['image/jpeg', 'image/png', 'image/webp'];
export const MAX_UPLOAD_SIZE_TENANT_LOGO = 1 * BYTES_PER_MEGABYTE;
export const ALLOWED_FILE_TYPES_TENANT_LOGO = ['image/png', 'image/jpeg'];
export const MAX_UPLOAD_SIZE_EXTERNAL_MATERIAL_LOGO = 1 * BYTES_PER_MEGABYTE;
export const ALLOWED_FILE_TYPES_EXTERNAL_MATERIAL_LOGO = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
export const MAX_UPLOAD_SIZE_REPORT_COVER_IMAGE = 1 * BYTES_PER_MEGABYTE;
export const ALLOWED_FILE_TYPES_REPORT_COVER_IMAGE = ['image/png', 'image/jpeg', 'image/webp'];
export type TFileUploadError = 'size' | 'type' | null;

export function sanitizeFileNamePart(identifier: string | number): string {
  return identifier.toString().replace(/[^a-zA-Z0-9-_.]/g, '-');
}

export function buildPdfFileName(prefix: string, identifier: string | number): string {
  return `${sanitizeFileNamePart(prefix)}-${sanitizeFileNamePart(identifier)}.pdf`;
}

export function isFileSizeExceedingLimit(fileSize: number, allowedBytes: number): boolean {
  return fileSize > allowedBytes;
}

export function isFileTypeAllowed(fileType: string, allowedFileTypes: string[]): boolean {
  return allowedFileTypes.includes(fileType);
}

export function formatSupportedFileTypes(fileTypes: string[]): string {
  const labels = fileTypes.map((fileType) => {
    switch (fileType) {
      case 'image/jpeg':
        return 'JPG';
      case 'image/png':
        return 'PNG';
      case 'image/gif':
        return 'GIF';
      case 'image/webp':
        return 'WebP';
      case 'application/pdf':
        return 'PDF';
      case 'application/msword':
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        return 'Word';
      case 'application/vnd.ms-excel':
      case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        return 'Excel';
      case 'image/*':
        return 'image';
      default:
        return fileType;
    }
  });

  const uniqueLabels = labels.filter((label, index) => labels.indexOf(label) === index);

  if (uniqueLabels.length === 0) {
    return '';
  }

  if (uniqueLabels.length === 1) {
    return uniqueLabels[0] ?? '';
  }

  return uniqueLabels.join(', ');
}

export function formatFileSize(fileSizeBytes: number): string {
  if (fileSizeBytes < BYTES_PER_KILOBYTE) {
    return `${fileSizeBytes} B`;
  }

  if (fileSizeBytes >= BYTES_PER_GIGABYTE) {
    const gigabytes = fileSizeBytes / BYTES_PER_GIGABYTE;
    return `${Number.isInteger(gigabytes) ? gigabytes.toString() : gigabytes.toFixed(1)} GB`;
  }

  if (fileSizeBytes >= BYTES_PER_MEGABYTE) {
    const megabytes = fileSizeBytes / BYTES_PER_MEGABYTE;
    return `${Number.isInteger(megabytes) ? megabytes.toString() : megabytes.toFixed(1)} MB`;
  }

  const kilobytes = fileSizeBytes / BYTES_PER_KILOBYTE;
  return `${Number.isInteger(kilobytes) ? kilobytes.toString() : kilobytes.toFixed(1)} KB`;
}
